import { useCallback, useContext, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext, { type OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { useToast } from '@/storybook/components/Toast/Toast';
import { readCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import collectAllLocalFilters from '@/utils/filter/collectAllLocalFilters';
import { savedFiltersEvents } from './savedFiltersEvents';
import fetchAndApplyFilter from '@/utils/filter/fetchAndApplyFilter';
import type { FilterState } from '@/types/filter/filterState';

interface SaveFiltersParams {
  filterName: string;
  isDefault: boolean;
}

interface UseSaveFiltersOptions {
  onSuccess?: () => void;
}

interface FailedSave {
  filterId: string;
}

interface UseSaveFiltersResult {
  handleSaveFilters: (params: SaveFiltersParams) => Promise<boolean>;
  isLoading: boolean;
  hasFailedSave: boolean;
  handleDeleteFailedSave: () => Promise<boolean>;
  isDeletingFailedSave: boolean;
}

export const READ_BACK_RETRY_DELAY_MS = 300;

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

type ReadBackParams = Pick<
  Parameters<typeof fetchAndApplyFilter>[0],
  'filterConfig' | 'handleApplyFilter' | 'replaceOptionPaths'
>;

type CreatedFilter = Awaited<ReturnType<typeof UserFilterApiService.createFilter>>['data'];

const getApiErrorMessage = (error: unknown): string | undefined =>
  axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
    ? error.response.data.message
    : undefined;

const buildSavePayload = (
  filterState: FilterState,
  optionPaths: OptionPathsByFilter,
  senderCountryCodes: string[],
): Record<string, unknown> => {
  const localFilters = collectAllLocalFilters();
  const resolvedCountryCodes = readCachedCountryCodes();

  return {
    globalFilters: filterState,
    ...(Object.keys(localFilters).length > 0 && { localFilters }),
    ...(resolvedCountryCodes.length > 0 && { resolvedCountryCodes }),
    ...(Object.keys(optionPaths).length > 0 && { optionPaths }),
    senderCountryCodes,
  };
};

const readBackSavedFilter = async (
  created: CreatedFilter,
  filterName: string,
  isDefault: boolean,
  readBackParams: ReadBackParams,
): Promise<void> => {
  const applyCreatedFilter = () =>
    fetchAndApplyFilter({
      filterId: created.uniqueFilterId,
      filterMetadata: {
        userFilterId: created.uniqueFilterId,
        filterName,
        filterType: created.filterType,
        filterCountries: created.filterCountries,
        filterDate: created.filterDate,
        isDefault,
      },
      ...readBackParams,
    });

  try {
    await applyCreatedFilter();
  } catch {
    await wait(READ_BACK_RETRY_DELAY_MS);
    await applyCreatedFilter();
  }
};

const useDeleteFailedSave = (
  failedSave: FailedSave | null,
  setFailedSave: Dispatch<SetStateAction<FailedSave | null>>,
) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const [isDeletingFailedSave, setIsDeletingFailedSave] = useState(false);
  const deletingRef = useRef(false);

  const handleDeleteFailedSave = useCallback(async (): Promise<boolean> => {
    if (deletingRef.current || failedSave === null) return false;
    const deletedId = failedSave.filterId;
    deletingRef.current = true;
    setIsDeletingFailedSave(true);
    try {
      await UserFilterApiService.deleteFilter(deletedId);
      setFailedSave(current => (current?.filterId === deletedId ? null : current));
      savedFiltersEvents.notify();
      return true;
    } catch {
      makeToast({
        content: String(t('global.filters.saveFiltersDialog.deleteFailedSaveError')),
        appearance: 'error',
      });
      return false;
    } finally {
      deletingRef.current = false;
      setIsDeletingFailedSave(false);
    }
  }, [failedSave, setFailedSave, makeToast, t]);

  return { handleDeleteFailedSave, isDeletingFailedSave };
};

const useSaveFilters = ({ onSuccess }: UseSaveFiltersOptions = {}): UseSaveFiltersResult => {
  const { t } = useTranslation();
  const { filterState, handleApplyFilter, filterConfig, optionPathsByFilter, replaceOptionPaths } =
    useContext(FiltersContext);
  const { userInfo } = useAuthContext();
  const { makeToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const savingRef = useRef(false);
  const [failedSave, setFailedSave] = useState<FailedSave | null>(null);
  const { handleDeleteFailedSave, isDeletingFailedSave } = useDeleteFailedSave(failedSave, setFailedSave);

  const handleSaveFilters = useCallback(
    async ({ filterName, isDefault }: SaveFiltersParams): Promise<boolean> => {
      if (savingRef.current || failedSave !== null) return false;
      savingRef.current = true;
      setIsLoading(true);
      let createdFilterId: string | undefined;
      try {
        const payload = buildSavePayload(filterState, optionPathsByFilter ?? {}, userInfo?.countries ?? []);

        const response = await UserFilterApiService.createFilter({
          filterType: 'saved_filter',
          filterName,
          isDefault,
          filters: payload,
        });

        createdFilterId = response.data.uniqueFilterId;

        await readBackSavedFilter(response.data, filterName, isDefault, {
          filterConfig,
          handleApplyFilter,
          replaceOptionPaths,
        });

        makeToast({
          content: String(t('global.filters.saveFiltersDialog.saveSuccess')),
          appearance: 'success',
          autoClose: 5000,
        });

        onSuccess?.();
        savedFiltersEvents.notify();
        return true;
      } catch (error) {
        if (createdFilterId !== undefined) {
          setFailedSave({ filterId: createdFilterId });
          savedFiltersEvents.notify();
        }
        makeToast({
          content: getApiErrorMessage(error) ?? String(t('global.filters.saveFiltersDialog.saveError')),
          appearance: 'error',
        });
        return false;
      } finally {
        savingRef.current = false;
        setIsLoading(false);
      }
    },
    [
      filterState,
      filterConfig,
      handleApplyFilter,
      optionPathsByFilter,
      replaceOptionPaths,
      userInfo,
      failedSave,
      makeToast,
      onSuccess,
      t,
    ],
  );

  return {
    handleSaveFilters,
    isLoading,
    hasFailedSave: failedSave !== null,
    handleDeleteFailedSave,
    isDeletingFailedSave,
  };
};

export default useSaveFilters;
